'use client';

import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import ControlLabel from './ControlLabel';

interface ControlledInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  name: string;
  type?: string;
  label?: string;
  rules?: any;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  value?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  size?: 'small' | 'medium';
  min?: number;
  max?: number;
  helperText?: string;
  disabled?: boolean;
  showLabel?: boolean;
  hasTooltip?: boolean;
  tooltipText?: string;
  useLabel?: boolean;
  fullWidth?: boolean;
  onBlur?: () => void;
  onChange?: (e) => void;
  onKeyDown?: (e) => void;
}

const ControlledInput = ({ ...props }: ControlledInputProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  if (!props) return null;

  const errorMessage = props?.helperText || 'This field is required';
  const required = props.required || props.rules?.required;
  const hasError = !!errors[props.name];
  const useLabel = props.useLabel !== undefined ? props.useLabel : true;
  const fullWidth = props.fullWidth !== undefined ? props.fullWidth : true;

  return (
    <Controller
      name={props.name}
      control={control}
      rules={{
        required: required ? errorMessage : false,
        ...props.rules,
      }}
      render={({ field }) => {
        const inputElement = (
          <input
            {...field}
            type={props.type || 'text'}
            value={field.value || ''}
            onKeyDown={props.onKeyDown || undefined}
            onChange={(e) => {
              field.onChange(e);
              if (props.onChange) props.onChange(e);
            }}
            onBlur={props.onBlur || field.onBlur}
            autoFocus={props.autoFocus}
            placeholder={props.placeholder || 'Enter a value...'}
            min={props.min}
            max={props.max}
            disabled={props.disabled || false}
            aria-label={props.label || ''}
            className={`${fullWidth ? 'w-full' : ''} px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 ${
              hasError
                ? 'outline-red-500'
                : 'outline-neutral-300'
            } text-primary text-base font-normal font-['Inter'] leading-normal disabled:cursor-not-allowed disabled:text-neutral-300 disabled:bg-neutral-50 ${
              props.className || ''
            }`}
          />
        );

        const errorElement = hasError && (
          <p className="text-red-500 text-xs mt-1 ml-0">
            {errors[props.name]?.message as string}
          </p>
        );

        if (useLabel) {
          return (
            <ControlLabel
              label={props?.label || ''}
              required={required}
              showLabel={props.showLabel}
              hasTooltip={props.hasTooltip}
              tooltipText={props.tooltipText}
            >
              {inputElement}
              {errorElement}
            </ControlLabel>
          );
        }

        return (
          <>
            {inputElement}
            {errorElement}
          </>
        );
      }}
    />
  );
};

export default ControlledInput;
